import { Card, Input, Space } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import type { OrderSnapshotVo, RecordOrderAuditForm } from "@zhihaoscm/entity";
import {
    Button as ZhButton,
    DetailHeader as ZhDetailHeader,
    Dialog as ZhDialog,
    Grid as ZhGrid,
    GridItem as ZhGridItem
} from "zhihao-ui-react";

import request from "@/api";

export interface TradeOrderAuditPageProps {
    loadSnapshot?: (id: string) => Promise<OrderSnapshotVo>;
    auditOrder?: (id: string, form: RecordOrderAuditForm) => Promise<unknown>;
}

/** 复用旧 Vue 补录订单审核页的订单快照接口。 */
export async function loadTradeOrderAuditSnapshot(id: string): Promise<OrderSnapshotVo> {
    const response = await request.service.get<OrderSnapshotVo>(`/admin/order-snapshot/vo/${id}`);
    return response.data;
}

/** 复用旧 Vue 补录订单审核接口，保持 state/auditDesc 字段结构。 */
export async function auditTradeOrderRecord(id: string, form: RecordOrderAuditForm): Promise<void> {
    await request.service.put(`/admin/order/audit/record-order/${id}`, form);
}

function valueOf(value: unknown): string {
    return value === undefined || value === null || value === "" ? "-" : String(value);
}

/** 审核快照字段使用单一文本节点，避免 beta.13 BaseItem 的无障碍镜像造成重复查询结果。 */
function AuditField({ label, value }: { label: string; value: string }) {
    return (
        <div>
            {label}: {value}
        </div>
    );
}

/** 独立补录订单审核页，保留旧 Vue 的快照区块和审核意见校验。 */
export function TradeOrderAuditPage({
    loadSnapshot = loadTradeOrderAuditSnapshot,
    auditOrder = auditTradeOrderRecord
}: TradeOrderAuditPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const id = new URLSearchParams(location.search).get("id") || "";
    const [snapshot, setSnapshot] = useState<OrderSnapshotVo | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [decision, setDecision] = useState<boolean | null>(null);
    const [auditDescription, setAuditDescription] = useState("");

    useEffect(() => {
        if (!id) {
            setErrorMessage("缺少订单编号");
            return;
        }
        let disposed = false;
        void loadSnapshot(id)
            .then(result => {
                if (!disposed) {
                    setSnapshot(result);
                    setErrorMessage(null);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("订单快照加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [id, loadSnapshot]);

    const submitAudit = async () => {
        if (decision == null) {
            setErrorMessage("请选择审核结果");
            return;
        }
        const description = auditDescription.trim();
        if (!decision && !description) {
            setErrorMessage("请输入驳回原因");
            return;
        }
        try {
            await auditOrder(id, { state: decision, auditDesc: description });
            setDecision(null);
            setAuditDescription("");
            setErrorMessage(null);
            void navigate(-1);
        } catch {
            setErrorMessage("订单审核失败");
        }
    };

    const order = snapshot?.order;
    return (
        <section className="detail-page-transparent flex min-h-full flex-col gap-4" aria-label="trade-order-audit-page">
            <ZhDetailHeader title="审核补录订单" />
            {errorMessage && errorMessage !== "请输入驳回原因" ? (
                <Alert message={errorMessage} role="alert" type="error" />
            ) : null}
            {order ? (
                <>
                    <Card title="商品信息">
                        <ZhGrid cols={4} gap={[24, 0]}>
                            <ZhGridItem span={1}>
                                <AuditField label="商品编号" value={valueOf(order.productId)} />
                            </ZhGridItem>
                            <ZhGridItem span={1}>
                                <AuditField label="商品名称" value={valueOf(order.productInfo?.title)} />
                            </ZhGridItem>
                            <ZhGridItem span={1}>
                                <AuditField label="采购数量(吨)" value={valueOf(order.ton)} />
                            </ZhGridItem>
                            <ZhGridItem span={1}>
                                <AuditField label="商品单价" value={valueOf(order.unitPrice)} />
                            </ZhGridItem>
                        </ZhGrid>
                    </Card>
                    <Card title="采购用户信息">
                        <ZhGrid cols={4} gap={[24, 0]}>
                            <ZhGridItem span={1}>
                                <AuditField label="用户名称" value={valueOf(order.customerEnterprise?.name)} />
                            </ZhGridItem>
                            <ZhGridItem span={1}>
                                <AuditField label="手机号" value={valueOf(order.customerEnterprise?.mobile)} />
                            </ZhGridItem>
                            <ZhGridItem span={2}>
                                <AuditField
                                    label="收货地址"
                                    value={valueOf(
                                        [
                                            order.addressInfo?.province,
                                            order.addressInfo?.city,
                                            order.addressInfo?.district,
                                            order.addressInfo?.detailAddress
                                        ]
                                            .filter(Boolean)
                                            .join("")
                                    )}
                                />
                            </ZhGridItem>
                        </ZhGrid>
                    </Card>
                    <Card title="收款信息">
                        <div>收款记录: {snapshot.receivePayments?.length || 0} 条</div>
                    </Card>
                    <Card title="提货信息">
                        <div>提货记录: {snapshot.deliveries?.length || 0} 条</div>
                    </Card>
                    <Card title="指派专员">
                        <div>订单专员: {valueOf(order.handlerName)}</div>
                    </Card>
                </>
            ) : null}
            <Space className="sticky bottom-0 mt-auto bg-white py-4">
                <ZhButton disabled={!snapshot} onClick={() => setDecision(true)}>
                    同意
                </ZhButton>
                <ZhButton disabled={!snapshot} onClick={() => setDecision(false)}>
                    拒绝
                </ZhButton>
                <ZhButton onClick={() => void navigate(-1)}>返回</ZhButton>
            </Space>
            <ZhDialog
                open={decision != null}
                title={decision ? "审核意见" : "驳回原因"}
                width={400}
                onCancel={() => setDecision(null)}
                footer={
                    <Space>
                        <ZhButton onClick={() => setDecision(null)}>取消</ZhButton>
                        <ZhButton type="primary" onClick={() => void submitAudit()}>
                            确认审核
                        </ZhButton>
                    </Space>
                }
            >
                {errorMessage === "请输入驳回原因" ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                <Input.TextArea
                    aria-label="审核意见"
                    maxLength={200}
                    placeholder={decision ? "请输入审核意见" : "请输入驳回原因"}
                    value={auditDescription}
                    onChange={event => setAuditDescription(event.target.value)}
                />
            </ZhDialog>
        </section>
    );
}

export default TradeOrderAuditPage;
